import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { createContext, useContextSelector } from 'use-context-selector';

import { getReservationIncidentReports } from '@/services/ReservationIncidentReports/request';
import {
  LaunchBOType,
  ReservationIncidentReportParams,
} from '@/services/ReservationIncidentReports/types';

import { useToast } from '@/context/ToastContext';
import { useUser } from '@/context/UserContext';

import { useMobile } from '@/hooks/useMobile/useMobile';

import { getMonitoringSheet, IMonitoringSheet } from '@/pages/Partners/utils/monitoringSheet';

type IHideElements = {
  footer?: { is: boolean; animate?: boolean };
  header?: { is: boolean; animate?: boolean };
  whatsapp?: { is: boolean; animate?: boolean };
};

type Welcome = { is: boolean };

interface ILoading {
  aux: boolean;
  main: boolean;
  reservationIncidentReports: boolean;
  message: string | undefined;
}

export interface IFilters {
  page: number;
  year?: number;
  month?: number;
  property_id?: string;
  property_code?: string;
  external_id?: string;
  guest_name?: string;
  incident_type?: string;
  page_size?: number;
  search_type?: string;
  search_str?: string;
  status: string;
  check_in_date?: string;
  check_out_date?: string;
  report_date?: string;
  under_review_reason?: string;
  approval_date?: string;
  denied_date?: string;
  timestamp?: number;
}

type LaunchBOCounts = {
  launchBO: number;
};

interface ILaunchBOContextProps {
  successScreen: boolean;
  setSuccessScreen: Function;
  reservationIncidentReports: LaunchBOType[];
  setReservationIncidentReports: React.Dispatch<React.SetStateAction<LaunchBOType[]>>;
  showBurgerMenu: boolean;
  setShowBurgerMenu: Function;
  monitoringSheet: () => IMonitoringSheet;
  hideElements: IHideElements;
  setHideElements: React.Dispatch<React.SetStateAction<IHideElements>>;
  questions: Questions;
  setQuestions: React.Dispatch<React.SetStateAction<Questions>>;
  welcome: Welcome;
  setWelcome: React.Dispatch<React.SetStateAction<Welcome>>;
  validateAction: boolean;
  loading: ILoading;
  setLoading: React.Dispatch<React.SetStateAction<ILoading>>;
  filters: IFilters;
  setFilters: Function;
  reservationIncidentReportsCounts: LaunchBOCounts;
  launchBOTotalPagesRef: React.MutableRefObject<number>;
  handleGetReservationIncidentReports: () => Promise<void>;
  isAddOpen: boolean;
  setIsAddOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const hideElementsInitial = {
  footer: { is: false, animate: true },
  header: { is: false, animate: true },
  whatsapp: { is: false, animate: true },
};

interface Questions {
  doesThePropertyHaveFurniture: boolean;
  isThePropertyUnderConstruction: boolean;
  isTheOwnerAwareOfTheIndication: boolean;
  IsIndicatedWithinSeazoneOperationArea: boolean;
}

export type KeyQuestions = keyof Questions;

export const LaunchBOContext = createContext<ILaunchBOContextProps>({} as ILaunchBOContextProps);

export const LaunchBOContextProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const timmer = useRef<NodeJS.Timeout>({} as NodeJS.Timeout);

  const launchBOTotalPagesRef = useRef<number>(1);
  const isFirstRender = useRef(true);

  const { isMobile } = useMobile();
  const { user, userInformation } = useUser();
  const navigate = useNavigate();
  const { filter } = useParams();
  const toast = useToast();

  const [filters, setFilters] = useState<IFilters>({
    page: 1,
    page_size: 10,
    status: 'All',
  });

  const [reservationIncidentReportsCounts, setReservationIncidentReportsCounts] =
    useState<LaunchBOCounts>({
      launchBO: 0,
    });

  const [loading, setLoading] = useState<ILoading>({
    main: false,
    aux: false,
    reservationIncidentReports: true,
    message: undefined,
  });
  const [successScreen, setSuccessScreen] = useState(false);
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [hideElements, setHideElements] = useState<IHideElements>(hideElementsInitial);
  const [welcome, setWelcome] = useState({ is: true });

  const [questions, setQuestions] = useState<Questions>({
    doesThePropertyHaveFurniture: false,
      IsIndicatedWithinSeazoneOperationArea: false,
    isTheOwnerAwareOfTheIndication: false,
    isThePropertyUnderConstruction: false,
  } as Questions);

  const [reservationIncidentReports, setReservationIncidentReports] = useState<LaunchBOType[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const handleSetFilters = useCallback((newFilters: IFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSuccessScreen = useCallback((newData: boolean) => {
    setSuccessScreen(newData);
  }, []);

  const handleShowBurgerMenu = useCallback((newData: boolean) => {
    setShowBurgerMenu(newData);
  }, []);

  const handleReservationIncidentReports = useCallback((newData: LaunchBOType[]) => {
    setReservationIncidentReports(newData);
  }, []);

  const monitoringSheet = useCallback(() => {
    const response = getMonitoringSheet(user);
    return response;
  }, [user]);

  const handleValidateAction = () => {
    const validateAction =
      userInformation?.required_actions?.Partner?.includes('PARTNER_WELCOME_POPUP');
    return validateAction || false;
  };

  const handleLoading = () => {
    setLoading((prev) => ({ ...prev, main: true, aux: true }));
  };

  const handleMessage = (message: string) => {
    setLoading((prevState) => ({ ...prevState, message }));
  };

  const handleStopLoading = () => {
    setLoading((prev) => ({
      ...prev,
      aux: false,
      main: false,
      message: undefined,
    }));
  };

  const validateAction = React.useMemo(() => handleValidateAction(), [userInformation]);

  const handleGetReservationIncidentReports = useCallback(async () => {
    setLoading((prev) => ({ ...prev, reservationIncidentReports: true }));

    try {
      const apiParams: ReservationIncidentReportParams = {
        page: filters.page,
        page_size: filters.page_size,
        year: filters.year,
        month: filters.month,
        property_id: filters.property_id,
        property_code: filters.property_code,
        external_id: filters.external_id,
        guest_name: filters.guest_name,
        incident_type: filters.incident_type,
        search_type: filters.search_type,
        search_str: filters.search_str,
        status: filters.status === 'All' ? undefined : filters.status,
        check_in_date: filters.check_in_date,
        check_out_date: filters.check_out_date,
        report_date: filters.report_date,
        under_review_reason: filters.under_review_reason,
        approval_date: filters.approval_date,
        denied_date: filters.denied_date,
      };
      const reservationIncidentReportsResult = await getReservationIncidentReports(apiParams);

      launchBOTotalPagesRef.current = reservationIncidentReportsResult.total_pages || 1;

      setReservationIncidentReportsCounts((prev) => ({
        ...prev,
        launchBO: reservationIncidentReportsResult.count || 0,
      }));

      const result: LaunchBOType[] = reservationIncidentReportsResult.results.map((item) => ({
        ...item,
      }));

      setReservationIncidentReports(result);
    } catch (e) {
      if (e instanceof AxiosError && e.response) {
        switch (e.response.status) {
          case 400:
            toast.error('Dados inválidos. Verifique os campos.');
            break;
          case 401:
            toast.error('Sessão expirada. Faça login novamente.');
            break;
          case 403:
            toast.error('Você não tem permissão para realizar esta ação.');
            break;
          case 404:
            toast.error('Lançamentos não encontrados.');
            break;
          case 500:
            toast.error('Erro interno no servidor. Tente novamente mais tarde.');
            break;
          default:
            toast.error(`Erro desconhecido: ${e.response.status}`);
        }
      } else if (e instanceof AxiosError && e.code === 'ERR_NETWORK') {
        toast.error('Sem conexão com a internet.');
      }
    } finally {
      setLoading((prev) => ({ ...prev, reservationIncidentReports: false }));
    }
  }, [filters, filter]);

  useEffect(() => {
    handleGetReservationIncidentReports();
  }, [JSON.stringify(filters), filter]);

  useEffect(() => {
    const handleValidateWelcomePage = () => {
      if (validateAction) {
        if (!isMobile && welcome.is) {
          return true;
        }
      }
      return false;
    };

    const handleGetInitialData = async () => {
      try {
        handleMessage('Carregando dados iniciais...');

        setReservationIncidentReportsCounts((prev) => ({
          ...prev,
        }));
      } catch (e) {
        console.error('Erro ao buscar dados iniciais:', e);
        toast.error('Não foi possível carregar os dados iniciais.');
      }
    };

    if (isFirstRender.current) {
      isFirstRender.current = false;
      try {
        handleLoading();
        Promise.all([handleGetInitialData(), handleValidateWelcomePage()]).then(() => {
          handleStopLoading();
        });
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        }
      }
    }

    return () => {
      clearTimeout(timmer.current);
    };
  }, [isMobile, navigate, toast, validateAction, welcome, userInformation]);

  // O bloco useEffect que causava a chamada duplicada ao mudar os filtros foi removido daqui.

  const value = useMemo(
    () => ({
      reservationIncidentReports,
      setReservationIncidentReports,
      handleReservationIncidentReports,
      successScreen,
      setSuccessScreen: handleSuccessScreen,
      showBurgerMenu,
      setShowBurgerMenu: handleShowBurgerMenu,
      monitoringSheet,
      hideElements,
      setHideElements,
      questions,
      setQuestions,
      welcome,
      setWelcome,
      validateAction,
      loading,
      setLoading,
      filters,
      setFilters: handleSetFilters,
      reservationIncidentReportsCounts,
      launchBOTotalPagesRef,
      handleGetReservationIncidentReports,
      isAddOpen,
      setIsAddOpen,
    }),
    [
      welcome,
      reservationIncidentReports,
      setReservationIncidentReports,
      handleReservationIncidentReports,
      successScreen,
      handleSuccessScreen,
      showBurgerMenu,
      handleShowBurgerMenu,
      monitoringSheet,
      hideElements,
      setHideElements,
      questions,
      setQuestions,
      validateAction,
      loading,
      setLoading,
      filters,
      handleSetFilters,
      reservationIncidentReportsCounts,
      handleGetReservationIncidentReports,
      isAddOpen,
    ],
  );

  return <LaunchBOContext.Provider value={value}>{children}</LaunchBOContext.Provider>;
};

export const useLaunchBO = () => {
  const launchBOTotalPagesRef = useContextSelector(
    LaunchBOContext,
    (state) => state.launchBOTotalPagesRef,
  );
  const filters = useContextSelector(LaunchBOContext, (state) => state.filters);
  const setFilters = useContextSelector(LaunchBOContext, (state) => state.setFilters);
  const successScreen = useContextSelector(LaunchBOContext, (state) => state.successScreen);
  const setSuccessScreen = useContextSelector(LaunchBOContext, (state) => state.setSuccessScreen);
  const questions = useContextSelector(LaunchBOContext, (state) => state.questions);
  const setQuestions = useContextSelector(LaunchBOContext, (state) => state.setQuestions);
  const reservationIncidentReports = useContextSelector(
    LaunchBOContext,
    (state) => state.reservationIncidentReports,
  );
  const setReservationIncidentReports = useContextSelector(
    LaunchBOContext,
    (state) => state.setReservationIncidentReports,
  );
  const showBurgerMenu = useContextSelector(LaunchBOContext, (state) => state.showBurgerMenu);
  const setShowBurgerMenu = useContextSelector(LaunchBOContext, (state) => state.setShowBurgerMenu);
  const monitoringSheet = useContextSelector(LaunchBOContext, (state) => state.monitoringSheet);
  const welcome = useContextSelector(LaunchBOContext, (state) => state.welcome);
  const setWelcome = useContextSelector(LaunchBOContext, (state) => state.setWelcome);
  const hideElements = useContextSelector(LaunchBOContext, (state) => state.hideElements);
  const setHideElements = useContextSelector(LaunchBOContext, (state) => state.setHideElements);
  const loading = useContextSelector(LaunchBOContext, (state) => state.loading);
  const setLoading = useContextSelector(LaunchBOContext, (state) => state.setLoading);
  const reservationIncidentReportsCounts = useContextSelector(
    LaunchBOContext,
    (state) => state.reservationIncidentReportsCounts,
  );
  const validateAction = useContextSelector(LaunchBOContext, (state) => state.validateAction);

  const handleGetReservationIncidentReports = useContextSelector(
    LaunchBOContext,
    (state) => state.handleGetReservationIncidentReports,
  );
  const isAddOpen = useContextSelector(LaunchBOContext, (state) => state.isAddOpen);
  const setIsAddOpen = useContextSelector(LaunchBOContext, (state) => state.setIsAddOpen);

  const handleHideAllElements = () => {
    setHideElements((old) => ({
      ...old,
      whatsapp: { is: true },
      header: { is: true },
      footer: { is: true },
    }));
  };

  const handleShowAllElements = () => {
    setHideElements((old) => ({
      ...old,
      whatsapp: { is: false },
      header: { is: false, animate: false },
      footer: { is: false, animate: false },
    }));
  };

  return {
    launchBOTotalPagesRef,
    filters,
    setFilters,
    validateAction,
    welcome,
    setWelcome,
    reservationIncidentReports,
    setReservationIncidentReports,
    successScreen,
    setSuccessScreen,
    showBurgerMenu,
    setShowBurgerMenu,
    monitoringSheet,
    hideElements,
    setHideElements,
    handleHideAllElements,
    handleShowAllElements,
    questions,
    setQuestions,
    loading,
    setLoading,
    reservationIncidentReportsCounts,
    getLaunchBOList: handleGetReservationIncidentReports,
    isAddOpen,
    setIsAddOpen,
  };
};
